/**
 * Capture health tab (admin only — ops surface over GET /api/admin/capture-health).
 *
 * "Is the supervise capture working?" — aggregates over live_call_logs:
 *   - Both-legs %: calls where BOTH rep + customer legs captured (the live
 *     gate's hard requirement — this IS the cutover health bar).
 *   - Rep-leg drop %: the leg that kills rep attribution when it drops.
 *   - Failure-reason histogram (non-'ok' supervise outcomes per leg).
 *   - Per-day both-legs trend (inline CSS bars, no chart lib).
 *   - Recent calls with per-leg outcomes.
 *
 * Instant hangups (under the min duration AND zero transcript lines) are
 * excluded server-side; the excluded count is shown for context.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card, SelectField, Badge } from "@/components/ui";
import { LoadingState } from "@/components/LoadingState";
import { ErrorState } from "@/components/ErrorState";
import { formatDateTime, msToClock } from "@/lib/format";

const DAYS_OPTIONS = [
  { value: "7", label: "Last 7 days" },
  { value: "14", label: "Last 14 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
];

/** Color a health percentage: green >= 90, amber >= 70, red below. */
function pctTone(pct: number): "success" | "warning" | "danger" {
  if (pct >= 90) return "success";
  if (pct >= 70) return "warning";
  return "danger";
}

function pctTextClass(pct: number): string {
  switch (pctTone(pct)) {
    case "success":
      return "text-emerald-600 dark:text-emerald-400";
    case "warning":
      return "text-amber-600 dark:text-amber-400";
    case "danger":
      return "text-red-600 dark:text-red-400";
  }
}

/** A per-leg outcome badge ('ok' green; anything else red with the reason). */
function LegBadge({ outcome }: { outcome: string | undefined }) {
  if (outcome === "ok") return <Badge tone="success">ok</Badge>;
  if (!outcome) return <Badge tone="muted">missing</Badge>;
  return <Badge tone="danger">{outcome}</Badge>;
}

export function CaptureHealthTab() {
  const [days, setDays] = useState(7);

  const query = useQuery({
    queryKey: ["admin", "capture-health", days],
    queryFn: ({ signal }) => api.adminCaptureHealth({ days, limit: 50 }, signal),
  });

  if (query.isLoading) {
    return <LoadingState label="Loading capture health…" />;
  }
  if (query.isError || !query.data) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }
  const data = query.data;

  const failureRows = Object.entries(data.failure_reasons).sort(
    (a, b) => b[1].rep + b[1].customer - (a[1].rep + a[1].customer),
  );
  const failureMax = Math.max(...failureRows.map(([, v]) => v.rep + v.customer), 1);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Capture health</h2>
          <p className="text-sm text-muted-foreground">
            Quality of the live supervise capture — both legs must land for a call to
            pass the live gate.
          </p>
        </div>
        <SelectField
          label="Window"
          options={DAYS_OPTIONS}
          value={String(days)}
          onChange={(e) => setDays(Number(e.target.value))}
          className="min-w-[150px]"
        />
      </div>

      {/* ── Headline rates ─────────────────────────────────────────────────── */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="flex flex-col gap-1">
          <p className="text-xs font-medium text-muted-foreground">Both legs captured</p>
          <p className={"text-2xl font-semibold " + pctTextClass(data.both_legs_pct)}>
            {data.both_legs_pct}%
          </p>
          <p className="text-xs text-muted-foreground">
            {data.both_legs_count} of {data.qualifying_calls} qualifying calls
          </p>
        </Card>
        <Card className="flex flex-col gap-1">
          <p className="text-xs font-medium text-muted-foreground">Rep-leg drops</p>
          <p
            className={
              "text-2xl font-semibold " + pctTextClass(100 - data.rep_leg_drop_pct)
            }
          >
            {data.rep_leg_drop_pct}%
          </p>
          <p className="text-xs text-muted-foreground">
            {data.rep_leg_drop_count} calls where the rep leg didn&apos;t land — these
            lose rep attribution
          </p>
        </Card>
        <Card className="flex flex-col gap-1">
          <p className="text-xs font-medium text-muted-foreground">Qualifying calls</p>
          <p className="text-2xl font-semibold">{data.qualifying_calls}</p>
          <p className="text-xs text-muted-foreground">
            {data.excluded_short} instant hangups excluded · {data.total_calls} total
          </p>
        </Card>
      </div>

      {data.truncated && (
        <p className="text-sm text-amber-600 dark:text-amber-400">
          This window has more live calls than the dashboard scans at once — rates may
          be incomplete. Narrow the window for exact numbers.
        </p>
      )}

      {/* ── Per-day trend ──────────────────────────────────────────────────── */}
      <Card className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold">Both-legs % by day</h3>
        {data.by_day.length === 0 ? (
          <p className="text-sm text-muted-foreground">No supervised calls in this window.</p>
        ) : (
          <>
            <div className="flex h-16 items-end gap-1" aria-hidden>
              {data.by_day.map((d) => (
                <div
                  key={d.day}
                  className={
                    "flex-1 rounded-t-sm " +
                    (d.calls === 0
                      ? "bg-muted"
                      : pctTone(d.both_legs_pct) === "success"
                        ? "bg-band-solid"
                        : pctTone(d.both_legs_pct) === "warning"
                          ? "bg-band-fair"
                          : "bg-band-weak")
                  }
                  style={{ height: `${Math.max(d.both_legs_pct, 6)}%` }}
                  title={`${d.day}: ${d.both_legs_pct}% (${d.both_legs}/${d.calls} calls)`}
                />
              ))}
            </div>
            <div className="flex justify-between text-[10px] text-muted-foreground">
              <span>{data.by_day[0]?.day}</span>
              <span>{data.by_day[data.by_day.length - 1]?.day}</span>
            </div>
          </>
        )}
      </Card>

      {/* ── Failure reasons ────────────────────────────────────────────────── */}
      <Card className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold">Failure reasons</h3>
        {failureRows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No leg failures in this window.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs text-muted-foreground">
              <tr>
                <th className="py-2 pr-3 font-medium">Outcome</th>
                <th className="py-2 pr-3 font-medium">Rep leg</th>
                <th className="py-2 pr-3 font-medium">Customer leg</th>
                <th className="py-2 font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {failureRows.map(([outcome, v]) => {
                const total = v.rep + v.customer;
                return (
                  <tr key={outcome} className="border-b last:border-0">
                    <td className="py-2 pr-3 font-mono text-xs">{outcome}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{v.rep}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{v.customer}</td>
                    <td className="py-2">
                      <span className="flex items-center gap-2">
                        <span className="w-6 font-medium">{total}</span>
                        <span
                          className="h-2 rounded-full bg-band-weak"
                          style={{ width: `${Math.max((total / failureMax) * 120, 6)}px` }}
                          aria-hidden
                        />
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {/* ── Recent calls ───────────────────────────────────────────────────── */}
      <Card className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold">Recent supervised calls</h3>
        {data.recent.length === 0 ? (
          <p className="text-sm text-muted-foreground">No supervised calls in this window.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b text-left text-xs text-muted-foreground">
                <tr>
                  <th className="py-2 pr-3 font-medium">When</th>
                  <th className="py-2 pr-3 font-medium">Ext</th>
                  <th className="py-2 pr-3 font-medium">Direction</th>
                  <th className="py-2 pr-3 font-medium">Customer</th>
                  <th className="py-2 pr-3 font-medium">Length</th>
                  <th className="py-2 pr-3 font-medium">Lines</th>
                  <th className="py-2 pr-3 font-medium">Rep leg</th>
                  <th className="py-2 pr-3 font-medium">Customer leg</th>
                  <th className="py-2 font-medium">Both</th>
                </tr>
              </thead>
              <tbody>
                {data.recent.map((r) => (
                  <tr key={r.id} className="border-b last:border-0">
                    <td className="py-2 pr-3 whitespace-nowrap">
                      {r.started_at ? formatDateTime(r.started_at) : "—"}
                    </td>
                    <td className="py-2 pr-3">{r.extension_number ?? "—"}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{r.direction ?? "—"}</td>
                    <td className="py-2 pr-3 text-muted-foreground">
                      {r.customer_phone ?? "—"}
                    </td>
                    <td className="py-2 pr-3 text-muted-foreground">
                      {r.duration_ms != null ? msToClock(r.duration_ms) : "—"}
                    </td>
                    <td className="py-2 pr-3 text-muted-foreground">
                      {r.transcript_lines ?? 0}
                    </td>
                    <td className="py-2 pr-3">
                      <LegBadge outcome={r.leg_outcomes.rep} />
                    </td>
                    <td className="py-2 pr-3">
                      <LegBadge outcome={r.leg_outcomes.customer} />
                    </td>
                    <td className="py-2">
                      {r.both_legs ? (
                        <Badge tone="success">yes</Badge>
                      ) : (
                        <Badge tone="danger">no</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
